// Event processor - processes game events and applies changes to map state

import { getBackendUrl } from '../lib/api.js';
import { mapState } from '../state/map-state.js';
import { applyChanges } from './change-applicator.js';

/**
 * @typedef {import('../types/jsdoc-types.js').GameEvent} GameEvent
 * @typedef {import('../types/jsdoc-types.js').Change} Change
 * @typedef {import('../types/jsdoc-types.js').MonsterPlacement} MonsterPlacement
 * @typedef {import('../types/jsdoc-types.js').Wall} Wall
 * @typedef {import('../types/jsdoc-types.js').Opening} Opening
 * @typedef {import('../types/jsdoc-types.js').Light} Light
 * @typedef {import('../types/jsdoc-types.js').TurnState} TurnState
 * @typedef {import('../types/jsdoc-types.js').InitiativeEntry} InitiativeEntry
 * @typedef {import('../types/jsdoc-types.js').Character} Character
 * @typedef {import('../state/map-state.js').Player} Player
 */

/**
 * @typedef {import('../state/map-state.js').MapVisibility} MapVisibility
 */

/**
 * @typedef {object} VisibilityData
 * @property {Array<{col: number, row: number, illumination?: string}>} [gridCells]
 * @property {Array<Wall>} [walls]
 * @property {Array<Opening>} [openings]
 * @property {Array<Light>} [lights]
 * @property {Array<MonsterPlacement>} [monsterPlacements]
 * @property {boolean} [personalRefresh] - When true on an op=add /map/visibility change, the recipient's monsterPlacements list is authoritative and the applicator REPLACES (vs. merges) so placements that left visibility get dropped. Set by the per-recipient fan-out, not by application code.
 */

/**
 * @typedef {object} AttackEventInfo
 * @property {'pc' | 'npc'} attackerType
 * @property {string} attackerId - Character ID or token ID of attacker
 * @property {'pc' | 'npc'} targetType
 * @property {string} targetId - Character ID or token ID of target
 * @property {string} targetName
 * @property {'hit' | 'miss' | 'graze'} outcome
 * @property {string} message
 * @property {number} [damage] - Damage dealt (for hits and grazes)
 */

/**
 * @typedef {object} EventProcessorOptions
 * @property {string} storagePrefix - 'editor' or 'player'
 * @property {string} storageId - Unique ID for localStorage scoping
 * @property {string} [backendUrl] - Backend URL for resolving image URLs
 * @property {(info: AttackEventInfo) => Promise<void>} [onAttackEvent] - Callback for attack events
 * @property {(message: { outcome: string; message: string }) => void} [onNPCMessage] - Callback for NPC messages
 * @property {(combatState: object) => void} [onCombatStarted] - Callback when combat starts
 * @property {(opts?: { banner?: { title?: string, subtitle?: string } | null }) => void} [onCombatEnded] - Callback when combat ends. `banner` is the server-rendered EventBanner attached to the event.
 * @property {(entry: object, opts?: { banner?: { title?: string, subtitle?: string } | null }) => void} [onTurnAdvanced] - Callback when combat turn advances to a new actor. `banner` is the server-rendered EventBanner attached to the event (nil = no banner; render default).
 * @property {() => void} [onSnapshotUpdate] - Called after every event is processed
 * @property {(summary: string, eventType: string) => void} [onEventSummary] - Called when an event has a player-friendly summary
 * @property {(callouts: Array<object>) => void} [onCallouts] - Called when an event has server-generated map callouts to render
 * @property {(opts?: { banner?: { title?: string, subtitle?: string } | null }) => void} [onWaitingRoomComplete] - Called when waiting room completes (all players joined). `banner` is the server-rendered EventBanner attached to the event.
 * @property {(info: { restarterUserId?: string; restarterDisplayName?: string }) => void} [onGameRestarted] - Called when the game owner restarts; consumer is expected to clear local state and reload
 */

/**
 * Resolve relative image URL to absolute URL
 * @param {string | undefined} url
 * @returns {string | undefined}
 */
function resolveImageUrl(url) {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  const backendUrl = getBackendUrl();
  return backendUrl ? backendUrl + url : url;
}

/**
 * Create an event processor for handling game events
 * @param {EventProcessorOptions} options
 */
export function createEventProcessor(options) {
  const {
    storagePrefix,
    storageId,
    onAttackEvent,
    onNPCMessage,
    onCombatStarted,
    onCombatEnded,
    onTurnAdvanced,
    onSnapshotUpdate,
    onEventSummary,
    onCallouts,
    onWaitingRoomComplete,
    onGameRestarted,
  } = options;

  /** @type {GameEvent[]} */
  let events = [];
  /** @type {number | null} */
  let lastEventId = null;
  /** @type {number | null} — tracks the latest event received (for gap detection), may be ahead of lastEventId */
  let lastReceivedEventId = null;
  let eventsLoading = false;
  let eventsLoaded = false;

  // Track locally-created event IDs for deduplication
  /** @type {Set<number>} */
  const localEventIds = new Set();

  // Event queue for sequential processing with animations
  /** @type {GameEvent[]} */
  const eventQueue = [];
  let isProcessingQueue = false;
  // Playback control (for spectator). Default-off; the queue runs as
  // before until a caller pauses it.
  let paused = false;
  let stepOnce = false;

  /**
   * Get localStorage key with prefix
   * @param {string} key
   */
  const getStorageKey = (key) => {
    const context = storagePrefix === 'editor' ? 'adventure' : 'game';
    return `rpgish-${context}-${storageId}-${key}`;
  };

  /**
   * Remove localStorage entries for other games/adventures to free space
   */
  function cleanupStaleStorage() {
    const currentPrefix = getStorageKey('');
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      // Remove rpgish data for other games/adventures, but keep current
      if (key.startsWith('rpgish-') && !key.startsWith(currentPrefix)) {
        keysToRemove.push(key);
      }
      // Also clean up old game view state and viewport data
      if (key.startsWith('game-') && key.endsWith('-view-state') && !key.includes(storageId)) {
        keysToRemove.push(key);
      }
      if (key.startsWith('d3-gamemap-viewport-') && !key.includes(storageId)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    if (keysToRemove.length > 0) {
      console.info(`[${storagePrefix}] Cleaned up ${keysToRemove.length} stale localStorage entries`);
    }
  }

  // === Individual Event Processors ===

  /**
   * Process monsterPlacement:added event
   * @param {Change[]} changes
   */
  async function processMonsterPlacementAdded(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    /** @type {MonsterPlacement} */
    const newPlacement = /** @type {MonsterPlacement} */ ({
      id: data.id,
      col: data.col,
      row: data.row,
      direction: data.direction || 'down',
      size: data.size || 'medium',
      imageUrl: resolveImageUrl(data.imageUrl) || resolveImageUrl(data.imageUploadId),
      name: data.label || data.name || 'Unknown',
      hasCircleBorder: data.hasCircleBorder || false,
      borderColor: data.borderColor || '#000000',
      currentHP: data.hp,
      maxHP: data.maxHp,
      ac: data.ac,
      slug: data.slug,
    });
    mapState.monsterPlacements = [...mapState.monsterPlacements, newPlacement];
  }

  /**
   * Process monsterPlacement:moved event
   * @param {Change[]} changes
   */
  async function processMonsterPlacementMoved(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    const placementId = data.monsterPlacementId || data.tokenId;
    mapState.monsterPlacements = mapState.monsterPlacements.map((p) =>
      p.id === placementId ? { ...p, col: data.toCol, row: data.toRow } : p,
    );
  }

  /**
   * Process monsterPlacement:deleted event
   * @param {Change[]} changes
   */
  async function processMonsterPlacementDeleted(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.monsterPlacements = mapState.monsterPlacements.filter((p) => p.id !== data.id);
  }

  /**
   * Process monsterPlacement:rotated event
   * @param {Change[]} changes
   */
  async function processMonsterPlacementRotated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    const placementId = data.monsterPlacementId || data.tokenId;
    mapState.monsterPlacements = mapState.monsterPlacements.map((p) =>
      p.id === placementId ? { ...p, direction: data.direction } : p,
    );
  }

  /**
   * Process monsterPlacement:updated event
   * @param {Change[]} changes
   */
  async function processMonsterPlacementUpdated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.monsterPlacements = mapState.monsterPlacements.map((p) => (p.id === data.id ? { ...p, ...data } : p));
  }

  /**
   * Process monsterPlacement-light:updated event
   * @param {Change[]} changes
   */
  async function processMonsterPlacementLightUpdated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    const placementId = data.monsterPlacementId || data.tokenId;
    mapState.monsterPlacements = mapState.monsterPlacements.map((p) =>
      p.id === placementId
        ? {
            ...p,
            givesLight: data.givesLight,
            brightRadius: data.brightRadius,
            dimRadius: data.dimRadius,
          }
        : p,
    );
  }

  /**
   * Process wall:added event
   * @param {Change[]} changes
   */
  async function processWallAdded(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.walls = [...mapState.walls, data];
  }

  /**
   * Process wall:deleted event
   * @param {Change[]} changes
   */
  async function processWallDeleted(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.walls = mapState.walls.filter((wall) => wall.id !== data.id);
  }

  /**
   * Process wall:updated event
   * @param {Change[]} changes
   */
  async function processWallUpdated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.walls = mapState.walls.map((wall) => (wall.id === data.id ? { ...wall, ...data } : wall));
  }

  /**
   * Process opening:added event
   * @param {Change[]} changes
   */
  async function processOpeningAdded(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.openings = [...mapState.openings, data];
  }

  /**
   * Process opening:deleted event
   * @param {Change[]} changes
   */
  async function processOpeningDeleted(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.openings = mapState.openings.filter((opening) => opening.id !== data.id);
  }

  /**
   * Process opening:toggled event
   * @param {Change[]} changes
   */
  async function processOpeningToggled(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.openings = mapState.openings.map((opening) =>
      opening.id === data.id ? { ...opening, isOpen: data.isOpen } : opening,
    );
  }

  /**
   * Process opening:updated event
   * @param {Change[]} changes
   */
  async function processOpeningUpdated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.openings = mapState.openings.map((opening) =>
      opening.id === data.id ? { ...opening, ...data } : opening,
    );
  }

  /**
   * Process light:added event
   * @param {Change[]} changes
   */
  async function processLightAdded(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.lights = [...mapState.lights, data];
  }

  /**
   * Process light:deleted event
   * @param {Change[]} changes
   */
  async function processLightDeleted(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.lights = mapState.lights.filter((light) => light.id !== data.id);
  }

  /**
   * Process light:toggled event
   * @param {Change[]} changes
   */
  async function processLightToggled(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.lights = mapState.lights.map((light) =>
      light.id === data.lightId ? { ...light, isOn: data.isOn } : light,
    );
  }

  /**
   * Process light:updated event
   * @param {Change[]} changes
   */
  async function processLightUpdated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.lights = mapState.lights.map((light) => (light.id === data.id ? { ...light, ...data } : light));
  }

  /**
   * Process cover:added event
   * @param {Change[]} changes
   */
  async function processCoverAdded(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.covers = [...mapState.covers, data];
  }

  /**
   * Process cover:deleted event
   * @param {Change[]} changes
   */
  async function processCoverDeleted(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.covers = mapState.covers.filter((cover) => cover.id !== data.id);
  }

  /**
   * Process map:created or map:set event
   * @param {Change[]} changes
   */
  async function processMapCreated(changes) {
    const data = changes[0]?.data;
    if (!data) return;

    mapState.gridSize = data.gridSize || 70;
    mapState.gridRows = data.gridRows || 20;
    mapState.gridCols = data.gridCols || 20;
    mapState.gridOffsetX = data.gridOffsetX || 0;
    mapState.gridOffsetY = data.gridOffsetY || 0;
    mapState.backgroundColor = data.backgroundColor || '#000000';
    mapState.backgroundUrl = resolveImageUrl(data.imageUrl || data.backgroundUrl || data.backgroundImageUrl) || '';
    mapState.globalIllumination = data.globalIllumination || 'bright';
    mapState.monsterPlacements = (data.monsterPlacements || []).map(
      /** @param {MonsterPlacement} p */ (p) => ({
        ...p,
        imageUrl: resolveImageUrl(p.imageUrl),
      }),
    );
    mapState.walls = data.walls || [];
    mapState.openings = data.openings || [];
    mapState.lights = data.lights || [];
    mapState.covers = data.covers || [];
    mapState.pcStartingPositions = data.pcStartingPositions || [];
  }

  /**
   * Process map-settings:updated event
   * @param {Change[]} changes
   */
  async function processMapSettingsUpdated(changes) {
    for (const change of changes) {
      const path = change.path || '';
      if (path.endsWith('/gridSize')) mapState.gridSize = change.data;
      if (path.endsWith('/gridRows')) mapState.gridRows = change.data;
      if (path.endsWith('/gridCols')) mapState.gridCols = change.data;
      if (path.endsWith('/backgroundColor')) mapState.backgroundColor = change.data;
      if (path.endsWith('/backgroundUrl')) mapState.backgroundUrl = resolveImageUrl(change.data) || '';
      if (path.endsWith('/globalIllumination')) mapState.globalIllumination = change.data;
    }
  }

  /**
   * Process pc-starting-positions:set event
   * @param {Change[]} changes
   */
  async function processPCStartingPositionsSet(changes) {
    const data = changes[0]?.data;
    if (data) {
      mapState.pcStartingPositions = data;
    }
  }

  /**
   * Process game:created event
   * @param {Change[]} changes
   */
  async function processGameCreated(changes) {
    for (const change of changes) {
      if (change.path === '/map/config' && change.data) {
        const config = change.data;
        mapState.gridSize = config.gridSize || 140;
        mapState.gridOffsetX = config.gridOffsetX || 0;
        mapState.gridOffsetY = config.gridOffsetY || 0;
        mapState.backgroundColor = config.backgroundColor || '#1a1a1a';
        mapState.backgroundUrl = config.backgroundImageUrl || '';
        if (config.gridRows) mapState.gridRows = config.gridRows;
        if (config.gridCols) mapState.gridCols = config.gridCols;
        if (config.globalIllumination) {
          mapState.globalIllumination = config.globalIllumination;
        }
      } else if (change.path === '/map/pcStartingPositions' && change.data) {
        // Backend sends a flat array of {col, row} coordinates.
        mapState.pcStartingPositions = change.data.map((pos) => ({ col: pos.col, row: pos.row }));
      } else if (change.path === '/hook' && change.data) {
        let imageURL = change.data.imageURL;
        if (imageURL) {
          imageURL = resolveImageUrl(imageURL);
        }
        mapState.hook = {
          text: change.data.text || '',
          imageURL: imageURL,
          title: change.data.title || undefined,
          buttonText: change.data.buttonText || undefined,
          dismissed: change.data.dismissed || false,
        };
      } else if (change.path === '/map/backgroundUrl') {
        mapState.backgroundUrl = resolveImageUrl(change.data) || '';
      } else if (change.path === '/waitingRoom' && change.data) {
        mapState.waitingRoom = change.data;
      } else if (change.path === '/game/objective' && change.data) {
        mapState.objective = change.data;
      } else if (change.path === '/seed') {
        mapState.seed = change.data;
      }
    }
  }

  /**
   * Process waiting-room:complete event
   * @param {Change[]} changes
   */
  async function processWaitingRoomReady(changes) {
    for (const change of changes) {
      if (change.path?.startsWith('/waitingRoom/seats/') && change.data) {
        if (mapState.waitingRoom) {
          const seatMatch = change.path.match(/\/waitingRoom\/seats\/(\d+)/);
          if (seatMatch) {
            const seatIndex = parseInt(seatMatch[1] || '', 10);
            const seats = [...mapState.waitingRoom.seats];
            seats[seatIndex] = change.data;
            mapState.waitingRoom = { ...mapState.waitingRoom, seats };
          }
        }
      }
    }
  }

  /**
   * @param {Change[]} changes
   */
  async function processWaitingRoomComplete(changes) {
    for (const change of changes) {
      if (change.path === '/waitingRoom/isActive') {
        if (mapState.waitingRoom) {
          mapState.waitingRoom = { ...mapState.waitingRoom, isActive: change.data };
        }
      } else if (change.path === '/map/config' && change.data) {
        const config = change.data;
        mapState.gridSize = config.gridSize || 140;
        mapState.gridOffsetX = config.gridOffsetX || 0;
        mapState.gridOffsetY = config.gridOffsetY || 0;
        if (config.gridRows) mapState.gridRows = config.gridRows;
        if (config.gridCols) mapState.gridCols = config.gridCols;
      } else if (change.path === '/map/backgroundUrl') {
        mapState.backgroundUrl = resolveImageUrl(change.data) || '';
      } else if (change.path === '/map/pcStartingPositions' && change.data) {
        mapState.pcStartingPositions = change.data.map((pos) => ({ col: pos.col, row: pos.row }));
      } else if (change.path === '/map/visibility' && change.data) {
        processVisibilitySet(change.data);
      } else if (change.path?.startsWith('/characters/') && change.path.endsWith('/position')) {
        // Clear character positions (set to null for adventure map placement)
        const match = change.path.match(/\/characters\/([^/]+)\/position/);
        if (match) {
          const characterId = match[1];
          const charIndex = mapState.playerCharacters.findIndex((c) => c.id === characterId);
          if (charIndex >= 0) {
            const chars = [...mapState.playerCharacters];
            const existingChar = chars[charIndex];
            if (!existingChar) throw new Error(`Expected character at index ${charIndex}`);
            chars[charIndex] = /** @type {Character} */ (
              /** @type {unknown} */ ({ ...existingChar, col: undefined, row: undefined })
            );
            mapState.playerCharacters = chars;
          }
        }
      }
    }
  }

  /**
   * Process pc:added event
   * @param {Change[]} changes
   */
  async function processPCAdded(changes) {
    for (const change of changes) {
      if (change.path === '/characters/-' && change.data) {
        // Resolve token imageUrl if present
        const tokenData = change.data.token
          ? {
              ...change.data.token,
              imageUrl: resolveImageUrl(change.data.token.imageUrl),
            }
          : undefined;

        const charData = {
          ...change.data,
          token: tokenData,
        };

        // If character has position (auto-placed in waiting room), include col/row on the character
        if (change.data.position) {
          const { col, row } = change.data.position;
          charData.col = col;
          charData.row = row;
        }

        mapState.playerCharacters = [...mapState.playerCharacters, charData];
      } else if (change.path?.startsWith('/waitingRoom/seats/') && change.data) {
        // Update waiting room seat occupancy
        if (mapState.waitingRoom) {
          const seatMatch = change.path.match(/\/waitingRoom\/seats\/(\d+)/);
          if (seatMatch) {
            const seatIndex = parseInt(seatMatch[1] || '', 10);
            const seats = [...mapState.waitingRoom.seats];
            seats[seatIndex] = change.data;
            mapState.waitingRoom = { ...mapState.waitingRoom, seats };
          }
        }
      } else if (change.path === '/map/visibility' && change.data) {
        processVisibilitySet(change.data);
      } else if (change.path?.startsWith('/characters/') && change.path?.endsWith('/data')) {
        // The post-handler pipeline can append a /characters/{id}/data change
        // to pc:added — e.g. to populate fields not in the initial pc:added
        // payload (RestActions for #381's prep phase). Defer to the generic
        // change-applicator so the merge logic stays in one place.
        applyChanges([change], mapState, { resolveImageUrl });
      }
    }
  }

  /**
   * Process visibility set (initial full visibility state)
   * @param {VisibilityData} visData
   */
  function processVisibilitySet(visData) {
    mapState.mapVisibility = /** @type {MapVisibility} */ ({
      gridCells: visData.gridCells || [],
      monsterPlacements: visData.monsterPlacements?.map((t) => ({ ...t })) || [],
      walls: visData.walls?.map((w) => ({ ...w })) || [],
      openings: visData.openings?.map((o) => ({ ...o })) || [],
      lights: visData.lights?.map((l) => ({ ...l })) || [],
    });

    // Also populate the main state arrays with visible elements
    if (visData.walls && visData.walls.length > 0) {
      mapState.walls = visData.walls;
    }
    if (visData.openings && visData.openings.length > 0) {
      mapState.openings = visData.openings;
    }
    if (visData.lights && visData.lights.length > 0) {
      mapState.lights = visData.lights;
    }
    if (visData.monsterPlacements && visData.monsterPlacements.length > 0) {
      mapState.monsterPlacements = visData.monsterPlacements.map(
        (p) =>
          /** @type {MonsterPlacement} */ ({
            ...p,
            imageUrl: resolveImageUrl(p.imageUrl),
          }),
      );
    }
  }

  /**
   * Process pc:moved event with step-by-step animation.
   * Groups changes into steps (each position change starts a new step),
   * then calls applyChanges for each step with a 500ms delay between steps.
   * @param {Change[]} changes
   */
  async function processPCMoved(changes) {
    console.debug('Processing pc:moved with', changes.length, 'changes');
    const steps = groupChangesIntoMovementSteps(changes);
    console.debug('Grouped into', steps.length, 'steps');

    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      if (stepIndex > 0) {
        console.debug(`Step ${stepIndex}: waiting 500ms`);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      applyChanges(steps[stepIndex], mapState, { resolveImageUrl });
    }

    // Surface any lastActionResult.message attached to the move (e.g.
    // the halt-on-reveal "You encounter a monster..." toast from #279).
    // Fired AFTER step animation so the message lands when the PC has
    // visually stopped, not partway through the walk.
    fireLastActionResultMessages(changes);
  }

  /**
   * Group a flat change list into movement steps. Each step begins at a
   * position change; non-position changes (visibility, conditions, etc.)
   * travel with the step they belong to.
   * @param {Change[]} changes
   * @returns {Change[][]}
   */
  function groupChangesIntoMovementSteps(changes) {
    /** @type {Change[][]} */
    const steps = [];
    /** @type {Change[]} */
    let currentStep = [];

    for (const c of changes) {
      if (c.path?.endsWith('/position') && c.data) {
        if (currentStep.length > 0) steps.push(currentStep);
        currentStep = [c];
      } else {
        currentStep.push(c);
      }
    }
    if (currentStep.length > 0) steps.push(currentStep);
    return steps;
  }

  /**
   * Fire the onAttackEvent callback for pc:attacked / pc:attack-missed.
   * PC attacks are fully server-driven via callouts now (see onCallouts in
   * the event queue loop), so this no longer constructs display text. It
   * still fires the callback with structured fields in case a future
   * consumer wants to react to hits/misses.
   *
   * @param {Change[]} changes
   * @param {string} eventType
   */
  async function firePCAttackedCallback(changes, eventType) {
    if (!onAttackEvent) return;
    if (eventType !== 'pc:attacked' && eventType !== 'pc:attack-missed') return;

    const lastActionChange = changes.find((c) => c.path?.endsWith('/lastActionResult'));
    const attackerMatch = lastActionChange?.path?.match(/\/characters\/([^/]+)\/lastActionResult/);
    if (!attackerMatch) return;
    const attackerId = attackerMatch[1];
    if (!attackerId) return;
    const lar = lastActionChange?.data || {};

    await onAttackEvent(
      /** @type {AttackEventInfo} */ ({
        attackerType: 'pc',
        attackerId,
        targetType: 'npc',
        targetId: lar.targetTokenId || '',
        targetName: '',
        outcome: eventType === 'pc:attacked' ? 'hit' : 'miss',
        message: '',
        damage: lar.damage,
      }),
    );
  }

  /**
   * Fire the onAttackEvent callback for monster:attacked / monster:attack-
   * missed / opportunity:attack / opportunity:attack-miss events. State
   * mutations on the target character already happened in applyChanges.
   * @param {Change[]} changes
   */
  async function fireNPCAttackedCallback(changes) {
    if (onAttackEvent) {
      const dataChange = changes.find((c) => c.path?.includes('/characters/') && c.path?.endsWith('/data'));
      if (dataChange?.path && dataChange?.data) {
        const charMatch = dataChange.path.match(/\/characters\/([^/]+)\/data/);
        if (charMatch) {
          const data = dataChange.data;
          const latestResult = data.combatResults?.[data.combatResults.length - 1];
          if (latestResult) {
            const targetCharId = charMatch[1];
            if (!targetCharId) throw new Error('Expected target character ID from path match');
            const targetChar = mapState.playerCharacters.find((c) => c.id === targetCharId);
            const targetName = targetChar?.name || 'target';

            const npcPlacementId = latestResult.attackerTokenId || '';

            const damage = latestResult.outcome === 'hit' ? latestResult.damage : undefined;

            await onAttackEvent(
              /** @type {AttackEventInfo} */ ({
                attackerType: 'npc',
                attackerId: npcPlacementId,
                targetType: 'pc',
                targetId: targetCharId,
                targetName,
                outcome: latestResult.outcome === 'hit' ? 'hit' : 'miss',
                message: latestResult.message || '',
                damage,
              }),
            );
          }
        }
      }
    }
  }

  /**
   * Process monster:moved event with step-by-step animation. Same shape
   * as processPCMoved — applyChanges per step with a 500ms delay.
   * @param {Change[]} changes
   */
  async function processMonsterMoved(changes) {
    const steps = groupChangesIntoMovementSteps(changes);
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      if (stepIndex > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      applyChanges(steps[stepIndex], mapState, { resolveImageUrl });
    }
  }

  /**
   * Fire the onNPCMessage callback for monster:turn-skipped. State already
   * handled (no-op — the event only carries an informational lastActionResult).
   * @param {Change[]} changes
   */
  function fireMonsterTurnSkippedCallback(changes) {
    if (!onNPCMessage) return;
    const lastActionChange = changes.find((c) => c.path?.endsWith('/lastActionResult'));
    if (lastActionChange?.data?.message) {
      onNPCMessage({
        outcome: lastActionChange.data.outcome || 'info',
        message: lastActionChange.data.message,
      });
    }
  }

  /**
   * Fire the onNPCMessage callback for any lastActionResult change that
   * carries a user-facing message. Used by dying-state events
   * (pc:fell-unconscious, pc:died, etc.) and effects-expired events.
   * State changes themselves are handled by applyChanges.
   * @param {Change[]} changes
   */
  function fireLastActionResultMessages(changes) {
    if (!onNPCMessage) return;
    for (const change of changes) {
      if (change.path?.endsWith('/lastActionResult') && change.data?.message) {
        onNPCMessage({
          outcome: change.data.outcome || 'info',
          message: change.data.message,
        });
      }
    }
  }

  /**
   * Process user:joined event
   * @param {Change[]} changes
   */
  async function processUserJoined(changes) {
    for (const change of changes) {
      if (change.path === '/players/-' && change.data) {
        const p = change.data;
        mapState.gamePlayers = [
          ...mapState.gamePlayers,
          {
            id: p.userId,
            username: p.username,
            role: p.role,
            avatarUrl: p.avatarUrl || '',
            connectionState: 'connected',
          },
        ];
        // Remove from invitedUsers since they've now joined
        mapState.invitedUsers = mapState.invitedUsers.filter((u) => u.userId !== p.userId);
      }
    }
  }

  /**
   * Process user:invited event
   * @param {Change[]} changes
   */
  async function processUserInvited(changes) {
    for (const change of changes) {
      if (change.path === '/invitedUsers/-' && change.data) {
        mapState.invitedUsers = [...mapState.invitedUsers, change.data];
      }
    }
  }

  // === Main Event Processing ===

  /**
   * Events whose change payloads are NOT path-based RFC-6902 changes
   * (i.e. changes[i].data is event-shaped, not {op, path, data}).
   * These are editor or bootstrap events that predate the backend change
   * applicator and stay on their custom per-event handlers. Anything not
   * in this set runs through applyChanges() first so state mutations are
   * handled orthogonally by path — the core invariant of issue #121.
   */
  const BOOTSTRAP_OR_EDITOR_EVENTS = new Set([
    // Editor map-editing events
    'monsterPlacement:added',
    'monsterPlacement:moved',
    'monsterPlacement:deleted',
    'monsterPlacement:rotated',
    'monsterPlacement:updated',
    'monsterPlacement-light:updated',
    'wall:added',
    'wall:deleted',
    'wall:updated',
    'opening:added',
    'opening:deleted',
    'opening:toggled',
    'opening:updated',
    'light:added',
    'light:deleted',
    'light:toggled',
    'light:updated',
    'cover:added',
    'cover:deleted',
    'map:created',
    'map:set',
    'map-settings:updated',
    'pc-starting-positions:set',
    // Bootstrap events (game setup / waiting room)
    'game:created',
    'game:state-sync',
    'spectator:sync',
    'pc:added',
    'waiting-room:ready',
    'waiting-room:complete',
    'user:joined',
    'user:invited',
    // Whole-object character/monster updates that predate path-based changes
    'pc:updated',
    'player-character:updated',
    // monster:equipment-changed used to ride the whole-object updater
    // (processMonsterPlacementUpdated), but the server now emits
    // path-based changes (/map/monsterPlacements/{id}/inventory,
    // /tokenSlug, /imageUrl). Including it here would skip the
    // path-based applyChanges at line ~1016 and leave the bow token
    // invisible until the next visibility recompute. Drop it from the
    // bootstrap set so applyChanges runs.
  ]);

  /**
   * Apply a single event's state mutation only — no animations, no
   * callbacks, no delays. Used during initial catch-up (REST backfill on
   * game enter, replay of locally-stored unprocessed events on refresh)
   * to rebuild the snapshot without re-playing the UX of past events.
   *
   * The flow mirrors applyEventLive but strips the UX side-effect paths:
   *   - Bootstrap/editor events dispatch to the same custom state handlers
   *     used by applyEventLive (those handlers are pure mapState mutations).
   *   - Movement events (pc:moved, monster:moved) apply all changes at
   *     once instead of stepwise — same final state, no animation timing.
   *   - adventure:completed sets the flag directly (the event type is the
   *     signal; the changes array is empty).
   *   - Everything else: applyChanges does the entire job.
   *
   * @param {GameEvent} event
   */
  async function applyEventToState(event) {
    const eventType = event.eventType;
    const changes = event.changes || [];

    if (eventType === 'adventure:completed') {
      mapState.adventureCompleted = true;
      return;
    }

    if (BOOTSTRAP_OR_EDITOR_EVENTS.has(eventType)) {
      switch (eventType) {
        case 'monsterPlacement:added':
          await processMonsterPlacementAdded(changes);
          break;
        case 'monsterPlacement:moved':
          await processMonsterPlacementMoved(changes);
          break;
        case 'monsterPlacement:deleted':
          await processMonsterPlacementDeleted(changes);
          break;
        case 'monsterPlacement:rotated':
          await processMonsterPlacementRotated(changes);
          break;
        case 'monsterPlacement:updated':
          await processMonsterPlacementUpdated(changes);
          break;
        case 'monsterPlacement-light:updated':
          await processMonsterPlacementLightUpdated(changes);
          break;
        case 'wall:added':
          await processWallAdded(changes);
          break;
        case 'wall:deleted':
          await processWallDeleted(changes);
          break;
        case 'wall:updated':
          await processWallUpdated(changes);
          break;
        case 'opening:added':
          await processOpeningAdded(changes);
          break;
        case 'opening:deleted':
          await processOpeningDeleted(changes);
          break;
        case 'opening:toggled':
          await processOpeningToggled(changes);
          break;
        case 'opening:updated':
          await processOpeningUpdated(changes);
          break;
        case 'light:added':
          await processLightAdded(changes);
          break;
        case 'light:deleted':
          await processLightDeleted(changes);
          break;
        case 'light:toggled':
          await processLightToggled(changes);
          break;
        case 'light:updated':
          await processLightUpdated(changes);
          break;
        case 'cover:added':
          await processCoverAdded(changes);
          break;
        case 'cover:deleted':
          await processCoverDeleted(changes);
          break;
        case 'map:created':
        case 'map:set':
          await processMapCreated(changes);
          break;
        case 'map-settings:updated':
          await processMapSettingsUpdated(changes);
          break;
        case 'pc-starting-positions:set':
          await processPCStartingPositionsSet(changes);
          break;
        case 'game:created':
          await processGameCreated(changes);
          break;
        case 'game:state-sync':
        case 'spectator:sync':
          await processGameCreated(changes);
          applyChanges(changes, mapState, { resolveImageUrl });
          break;
        case 'pc:added':
          await processPCAdded(changes);
          break;
        case 'waiting-room:ready':
          await processWaitingRoomReady(changes);
          break;
        case 'waiting-room:complete':
          await processWaitingRoomComplete(changes);
          break;
        case 'user:joined':
          await processUserJoined(changes);
          break;
        case 'user:invited':
          await processUserInvited(changes);
          break;
        case 'pc:updated':
        case 'player-character:updated':
        case 'monster:equipment-changed':
          await processMonsterPlacementUpdated(changes);
          break;
      }
      return;
    }

    // Everything else: changes are the full state mutation. Includes
    // pc:moved / monster:moved — applied all at once instead of stepwise.
    applyChanges(changes, mapState, { resolveImageUrl });
  }

  /**
   * Apply a live event: mutate state AND fire UX side effects (animation
   * delays, attack/message callbacks). Used for events the user is
   * watching happen in real time. For batch state rebuild during initial
   * catch-up, use applyEventToState — that path skips this function
   * entirely so UX callbacks don't fire for past events.
   *
   * The flow:
   *   1. For game-runtime events, call applyChanges() to update mapState
   *      by path. This is orthogonal to the event type, so an unknown event
   *      type that carries known paths still updates state correctly.
   *   2. The switch below then runs event-type-specific visual effects and
   *      callback fires. For game-runtime events these cases are callback-
   *      only. For editor/bootstrap events in BOOTSTRAP_OR_EDITOR_EVENTS,
   *      the cases perform their custom event-shaped state mutations.
   *
   * Movement events (pc:moved, monster:moved) are a special case because
   * they interleave state changes with per-step visual delays; they call
   * applyChanges inside their own step loop and skip phase 1 here.
   *
   * @param {GameEvent} event
   */
  async function applyEventLive(event) {
    const eventType = event.eventType;
    const changes = event.changes || [];

    console.debug('Processing event:', eventType, 'with', changes.length, 'changes');

    const needsStepwiseApply = eventType === 'pc:moved' || eventType === 'monster:moved';

    // Phase 1: apply path-based changes orthogonally. Skip editor/bootstrap
    // events and movement events (which handle their own stepping).
    if (!needsStepwiseApply && !BOOTSTRAP_OR_EDITOR_EVENTS.has(eventType)) {
      applyChanges(changes, mapState, { resolveImageUrl });
    }

    switch (eventType) {
      case 'monsterPlacement:added':
        await processMonsterPlacementAdded(changes);
        break;
      case 'monsterPlacement:moved':
        await processMonsterPlacementMoved(changes);
        break;
      case 'monsterPlacement:deleted':
        await processMonsterPlacementDeleted(changes);
        break;
      case 'monsterPlacement:rotated':
        await processMonsterPlacementRotated(changes);
        break;
      case 'monsterPlacement:updated':
        await processMonsterPlacementUpdated(changes);
        break;
      case 'monsterPlacement-light:updated':
        await processMonsterPlacementLightUpdated(changes);
        break;

      case 'wall:added':
        await processWallAdded(changes);
        break;
      case 'wall:deleted':
        await processWallDeleted(changes);
        break;
      case 'wall:updated':
        await processWallUpdated(changes);
        break;

      case 'opening:added':
        await processOpeningAdded(changes);
        break;
      case 'opening:deleted':
        await processOpeningDeleted(changes);
        break;
      case 'opening:toggled':
        await processOpeningToggled(changes);
        break;
      case 'opening:updated':
        await processOpeningUpdated(changes);
        break;

      case 'light:added':
        await processLightAdded(changes);
        break;
      case 'light:deleted':
        await processLightDeleted(changes);
        break;
      case 'light:toggled':
        await processLightToggled(changes);
        break;
      case 'light:updated':
        await processLightUpdated(changes);
        break;

      case 'cover:added':
        await processCoverAdded(changes);
        break;
      case 'cover:deleted':
        await processCoverDeleted(changes);
        break;

      case 'map:created':
      case 'map:set':
        await processMapCreated(changes);
        break;
      case 'map-settings:updated':
        await processMapSettingsUpdated(changes);
        break;

      case 'pc-starting-positions:set':
        await processPCStartingPositionsSet(changes);
        break;

      case 'game:created':
        await processGameCreated(changes);
        break;
      case 'game:state-sync':
      case 'spectator:sync':
        await processGameCreated(changes);
        applyChanges(changes, mapState, { resolveImageUrl });
        break;
      case 'hook:dismissed':
        // State handled by applyChanges (/hook/dismissed path).
        break;
      case 'waiting-room:ready':
        await processWaitingRoomReady(changes);
        break;
      case 'waiting-room:complete':
        await processWaitingRoomComplete(changes);
        break;
      case 'adventure:completed':
        // The backend emits adventure:completed with an empty Changes array —
        // the event type itself is the signal. Set the flag here since no
        // path-based change carries it.
        mapState.adventureCompleted = true;
        break;

      case 'game:party-wiped':
        // State handled by applyChanges (/partyWiped path).
        break;

      case 'game:restarted': {
        // Owner restarted the adventure. Every connected client (owner
        // included) reloads to pick up the freshly-recreated state.
        // The /restart change carries the restarter's identity for the
        // post-reload banner.
        const restartChange = (changes || []).find((/** @type {{ path?: string }} */ c) => c?.path === '/restart');
        const data =
          /** @type {{ data?: { restarterUserId?: string; restarterDisplayName?: string } }} */ restartChange?.data;
        onGameRestarted?.({
          restarterUserId: data?.restarterUserId,
          restarterDisplayName: data?.restarterDisplayName,
        });
        break;
      }

      case 'pc:added':
        await processPCAdded(changes);
        break;
      case 'pc:position-set':
        // State handled by applyChanges. No per-event code needed.
        break;
      case 'pc:moved':
        await processPCMoved(changes);
        break;
      case 'pc:rotated':
        // /characters/{id}/direction + /map/monsterPlacements/{id}/direction
        // are both handled by applyChanges.
        break;
      case 'pc:updated':
      case 'player-character:updated':
        await processMonsterPlacementUpdated(changes);
        break;

      case 'pc:attacked':
      case 'pc:attack-missed':
      case 'pc:cast-spell':
        await firePCAttackedCallback(changes, eventType);
        break;

      case 'pc:equipment-changed':
      case 'pc:item-used':
      case 'pc:used-equipment':
      case 'pc:dodged':
      case 'pc:disengaged':
      case 'pc:second-wind':
      case 'pc:hidden':
      case 'pc:found':
      case 'pc:readied':
        // Inventory, actions, bonusActions, ac, data, visibility, and light-
        // property changes are all handled by applyChanges. The Hide d20
        // roll surfaces via the existing /characters/{id}/lastActionResult
        // pipeline, and the Invisible badge appears once /conditions sets it.
        // Readied state lives on /characters/{id}/readied and is picked up
        // by applyChanges (#266); no extra callback needed.
        break;

      // Dying-state events. HP / conditions / death save counters update
      // via applyChanges; the callback here is just for floating messages.
      case 'pc:fell-unconscious':
      case 'pc:died':
      case 'pc:stabilized':
      case 'pc:stabilize-failed':
      case 'pc:regained-consciousness':
      case 'pc:death-save-rolled':
        fireLastActionResultMessages(changes);
        break;

      case 'monster:attacked':
      case 'monster:attack-missed':
      case 'opportunity:attack':
      case 'opportunity:attack-miss':
        fireNPCAttackedCallback(changes);
        break;
      case 'monster:moved':
        await processMonsterMoved(changes);
        break;
      case 'monster:equipment-changed':
        // Path-based applyChanges (run earlier in applyEventLive) writes
        // the new tokenSlug + imageUrl onto the placement; the renderer
        // picks it up from the next requestAnimationFrame tick. No
        // per-event handler needed here.
        break;
      case 'monster:turn-skipped':
        fireMonsterTurnSkippedCallback(changes);
        break;

      case 'monster-phase:started':
      case 'monster-phase:ended':
        // Informational events - no state changes
        break;

      case 'user:joined':
        await processUserJoined(changes);
        break;
      case 'user:invited':
        await processUserInvited(changes);
        break;

      case 'turn:ended':
      case 'combat:turn-ended':
      case 'combat:started':
      case 'combat:ended':
      case 'combat-initiative:set':
      case 'combat-turn:advanced':
      case 'combat:turn-started':
      case 'combat:turn-interrupted':
      case 'combat-participant:added':
      case 'combat-participant:removed':
        // All /turnState changes handled by applyChanges. Visual-effect
        // callbacks (onCombatStarted, onTurnAdvanced, etc.) fire from
        // applyVisualEffects() AFTER applyEventLive returns.
        break;

      case 'dice:rolled':
        // Dice rolls are informational
        break;

      case 'spell:effects-expired':
      case 'character:effects-expired':
        fireLastActionResultMessages(changes);
        break;

      case 'adventure:created':
      case 'pc-token:placed':
        // No-op events
        break;

      case 'player-connection:updated':
        // State handled by applyChanges (/players/{id}/connectionState).
        break;

      default:
        console.warn('Unhandled event type:', eventType);
    }

    // Note: /turnState and /characters/{id}/data used to be re-processed here
    // as a fallback for events whose per-type processor didn't look at them.
    // That fallback is gone — applyChanges() at the top of this function is
    // now the canonical state-mutation path.
  }

  /**
   * Fire UX callbacks and delay for visual effects.
   * Called AFTER the snapshot is saved, so refresh during delays is safe.
   * @param {string} eventType
   */
  async function applyVisualEffects(eventType, event) {
    if (eventType === 'waiting-room:complete') {
      const banner = /** @type {*} */ (event)?.banner || null;
      onWaitingRoomComplete?.({ banner });
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 2500));
    } else if (eventType === 'combat:started') {
      // Always-on initiative (#358) lifted initiative state up to the
      // top-level turnState. The reveal callback wants the same shape it
      // used to receive (initiativeOrder + initiator/target banner
      // fields), so build a transient projection from the flat fields.
      // Server-rendered banners (#306) thread the per-recipient banner
      // text through here so the reveal panel renders the server's
      // title verbatim instead of building one locally.
      const ts = /** @type {*} */ (mapState.turnState || {});
      const combatState = {
        initiativeOrder: ts.initiativeOrder || [],
        currentIndex: ts.currentIndex ?? 0,
        roundNumber: ts.roundNumber ?? 1,
        initiatorId: ts.initiatorId,
        initiatorType: ts.initiatorType,
        initiatorName: ts.initiatorName,
        targetId: ts.targetId,
        targetType: ts.targetType,
        targetName: ts.targetName,
        banner: /** @type {*} */ (event)?.banner || null,
      };
      const revealPromise = /** @type {Promise<void> | void | undefined} */ (onCombatStarted?.(combatState));
      mapState.notify();
      if (revealPromise && typeof (/** @type {Promise<void>} */ (revealPromise).then) === 'function') {
        await revealPromise;
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2500));
      }
    } else if (eventType === 'combat:ended') {
      const banner = /** @type {*} */ (event)?.banner || null;
      onCombatEnded?.({ banner });
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } else if (eventType === 'combat:turn-started' || eventType === 'combat-initiative:set') {
      // Always-on initiative (#358): combat:turn-started is the
      // canonical "new turn begins" signal for both PCs (emitted by
      // combat_advance_turn) and NPCs (emitted by runNPCCombatTurn).
      // combat:turn-ended is no longer used for the announcement —
      // it's just the housekeeping change set for the previous actor
      // ending. This split avoids the stale-actor double-announce
      // problem fan-out's pointer-drop produces for unseen monsters.
      const ts = /** @type {*} */ (mapState.turnState);
      const io = /** @type {Array<InitiativeEntry> | undefined} */ (ts?.initiativeOrder);
      const currentActorID = /** @type {string | undefined} */ (ts?.currentActorID);
      let entry = null;
      if (io && currentActorID) {
        entry = io.find((e) => e.actorId === currentActorID) || null;
      }
      if (!entry && io && typeof ts?.currentIndex === 'number') {
        entry = io[ts.currentIndex] || null;
      }
      if (entry) {
        // Server-rendered turn-start banner (#306 pattern): when the
        // server attached a Banner to this combat:turn-started (e.g.
        // "Ready triggered!" for the wait-for-pc readier's interrupt
        // turn), surface it to the callback so the announcement reads
        // the server's text verbatim. nil banner = fall back to the
        // callback's default ("Your Turn" / "{Name}'s Turn").
        const banner = /** @type {*} */ (event)?.banner || null;
        onTurnAdvanced?.(entry, { banner });
        mapState.notify();
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } else if (
      eventType === 'monster:attacked' ||
      eventType === 'monster:attack-missed' ||
      eventType === 'opportunity:attack' ||
      eventType === 'opportunity:attack-miss'
    ) {
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } else if (eventType === 'monster:turn-skipped') {
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } else if (eventType === 'monster:equipment-changed') {
      // The change-applicator already wrote the new tokenSlug + imageUrl
      // onto the placement object; without a notify here, the renderer
      // never re-binds and the on-screen token keeps the old silhouette
      // until some unrelated event (a PC move, etc.) forces a redraw.
      mapState.notify();
    } else if (eventType === 'pc:death-save-rolled') {
      // Hold the queue while the d20 die animation in the turn banner
      // spins and settles. Total animation budget: remainingSpin (750ms)
      // + settle hold (2500ms) = 3250ms, but the spin started on click
      // (before the event arrived), so subtract ~200ms for the API
      // round trip.
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 3250));
    } else if (eventType === 'pc:fell-unconscious') {
      mapState.notify();
      // TODO(death-saves-audio): dramatic sting here
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } else if (eventType === 'pc:died') {
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } else if (eventType === 'pc:stabilized' || eventType === 'pc:regained-consciousness') {
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } else if (eventType === 'game:party-wiped') {
      mapState.notify();
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  /**
   * Process the event queue sequentially
   */
  async function processEventQueue() {
    if (isProcessingQueue) return;
    isProcessingQueue = true;

    try {
      while (eventQueue.length > 0) {
        if (paused && !stepOnce) break;
        const wasStep = stepOnce;
        stepOnce = false;
        const event = eventQueue.shift();
        if (event) {
          // For waiting-room:complete, show announcement on the tavern map
          // BEFORE transitioning to the adventure map
          if (event.eventType === 'waiting-room:complete') {
            await applyVisualEffects(event.eventType, event);
          }

          await applyEventLive(event);

          // Fire summary callback if the event has one
          if (event.summary && onEventSummary) {
            onEventSummary(event.summary, event.eventType);
          }

          // Fire callouts (server-driven floating text). The queue only runs
          // for live events; historical replay uses applyEventToState which
          // never reaches this code, so no replay guard is needed.
          if (onCallouts && Array.isArray(event.callouts) && event.callouts.length > 0) {
            onCallouts(event.callouts);
          }

          // Save state + lastEventId atomically before visual delays
          lastEventId = event.id;
          saveStateSnapshot();
          saveLastEventId();
          mapState.notify();
          onSnapshotUpdate?.();

          // Visual effects (delays) — safe to interrupt
          if (event.eventType !== 'waiting-room:complete') {
            await applyVisualEffects(event.eventType, event);
          }

          // Uniform breathing room between events so consecutive actions
          // don't blur into each other and the renderer always paints
          // the post-event state before the next event mutates it.
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        if (wasStep) break;
      }
    } catch (error) {
      console.error(`[${storagePrefix}] Error processing event queue:`, error);
    } finally {
      isProcessingQueue = false;
    }
  }

  // === Storage Functions ===

  /**
   * Save state snapshot to localStorage
   */
  function saveStateSnapshot() {
    const snapshot = {
      walls: mapState.walls,
      monsterPlacements: mapState.monsterPlacements,
      backgroundUrl: mapState.backgroundUrl,
      gridSize: mapState.gridSize,
      gridRows: mapState.gridRows,
      gridCols: mapState.gridCols,
      backgroundColor: mapState.backgroundColor,
      gamePlayers: mapState.gamePlayers,
      playerCharacters: mapState.playerCharacters,
      invitedUsers: mapState.invitedUsers,
      openings: mapState.openings,
      lights: mapState.lights,
      covers: mapState.covers,
      globalIllumination: mapState.globalIllumination,
      pcStartingPositions: mapState.pcStartingPositions,
      turnState: mapState.turnState,
      hook: mapState.hook,
      objective: mapState.objective,
      adventureCompleted: mapState.adventureCompleted,
      partyWiped: mapState.partyWiped,
      mapVisibility: mapState.mapVisibility,
      waitingRoom: mapState.waitingRoom,
      seed: mapState.seed,
    };
    try {
      localStorage.setItem(getStorageKey('snapshot'), JSON.stringify(snapshot));
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.warn(`[${storagePrefix}] localStorage full, cleaning up stale data`);
        cleanupStaleStorage();
        try {
          localStorage.setItem(getStorageKey('snapshot'), JSON.stringify(snapshot));
        } catch {
          console.error(`[${storagePrefix}] localStorage still full after cleanup`);
        }
      }
    }
  }

  /**
   * Load state snapshot from localStorage
   */
  function loadStateSnapshot() {
    const stored = localStorage.getItem(getStorageKey('snapshot'));
    if (stored) {
      try {
        const snapshot = JSON.parse(stored);
        mapState.walls = snapshot.walls || [];
        mapState.monsterPlacements = snapshot.monsterPlacements || [];
        mapState.backgroundUrl = snapshot.backgroundUrl || '';
        mapState.gridSize = snapshot.gridSize || 70;
        mapState.gridRows = snapshot.gridRows || 20;
        mapState.gridCols = snapshot.gridCols || 20;
        mapState.backgroundColor = snapshot.backgroundColor || '#1a1a2e';
        mapState.gamePlayers = (snapshot.gamePlayers || []).map(
          /** @param {Player} p */ (p) => ({
            id: p.id,
            username: p.username,
            role: p.role,
            avatarUrl: p.avatarUrl || '',
            connectionState: 'connected',
          }),
        );
        mapState.playerCharacters = snapshot.playerCharacters || [];
        mapState.invitedUsers = snapshot.invitedUsers || [];
        mapState.openings = snapshot.openings || [];
        mapState.lights = snapshot.lights || [];
        mapState.covers = snapshot.covers || [];
        mapState.globalIllumination = snapshot.globalIllumination || 'bright';
        mapState.pcStartingPositions = snapshot.pcStartingPositions || [];
        mapState.turnState = snapshot.turnState || null;
        mapState.hook = snapshot.hook || null;
        mapState.objective = snapshot.objective || null;
        mapState.adventureCompleted = snapshot.adventureCompleted || false;
        mapState.partyWiped = snapshot.partyWiped || false;
        mapState.mapVisibility = snapshot.mapVisibility || null;
        mapState.waitingRoom = snapshot.waitingRoom || null;
        mapState.seed = snapshot.seed ?? null;
        mapState.notify();
      } catch (error) {
        console.error(`[${storagePrefix}] Error loading snapshot:`, error);
      }
    }
  }

  /**
   * Save events array to localStorage
   */
  function saveEventsArray() {
    try {
      localStorage.setItem(getStorageKey('events'), JSON.stringify(events));
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.warn(`[${storagePrefix}] localStorage full saving events, cleaning up`);
        cleanupStaleStorage();
        try {
          localStorage.setItem(getStorageKey('events'), JSON.stringify(events));
        } catch {
          console.error(`[${storagePrefix}] localStorage still full after cleanup`);
        }
      }
    }
  }

  /**
   * Load events array from localStorage
   */
  function loadEventsArray() {
    const stored = localStorage.getItem(getStorageKey('events'));
    if (stored) {
      try {
        events = JSON.parse(stored);
      } catch (error) {
        console.error(`[${storagePrefix}] Error loading events:`, error);
        events = [];
      }
    }
  }

  /**
   * Save last event ID to localStorage
   */
  function saveLastEventId() {
    if (lastEventId !== null) {
      try {
        localStorage.setItem(getStorageKey('lastEventId'), lastEventId.toString());
      } catch {
        // lastEventId is small, if this fails storage is very full
      }
    }
  }

  /**
   * Load last event ID from localStorage
   */
  function loadLastEventId() {
    const stored = localStorage.getItem(getStorageKey('lastEventId'));
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) {
        lastEventId = parsed;
        lastReceivedEventId = parsed;
      }
    }
  }

  // === Public API ===

  /**
   * Load events from localStorage
   */
  async function loadEvents() {
    eventsLoading = true;
    try {
      loadStateSnapshot();
      loadEventsArray();
      loadLastEventId();

      // Backfill seed from cached events if it's missing from the snapshot.
      // Older clients silently dropped the /seed change; existing games
      // would otherwise need a fresh start to show the debug seed overlay.
      if (mapState.seed == null && events.length > 0) {
        for (const event of events) {
          const seedChange = (event.changes || []).find((c) => c.path === '/seed');
          if (seedChange) {
            mapState.seed = seedChange.data;
            break;
          }
        }
      }

      // Replay any events that were stored but not yet processed (e.g.
      // page refreshed between receiving events and processing them).
      // Use applyEventToState to rebuild the snapshot without re-firing
      // animations / messages / callouts for past events.
      const unprocessed = events.filter((e) => lastEventId !== null && e.id > lastEventId);
      if (unprocessed.length > 0) {
        unprocessed.sort((a, b) => a.id - b.id);
        console.info(`[${storagePrefix}] Catching up ${unprocessed.length} unprocessed events`);
        for (const event of unprocessed) {
          await applyEventToState(event);
          lastEventId = event.id;
        }
        saveStateSnapshot();
        saveLastEventId();
        mapState.notify();
      }

      console.info(`[${storagePrefix}] Loaded snapshot with lastEventId=${lastEventId}`);
    } catch (error) {
      console.error(`[${storagePrefix}] Error loading events:`, error);
    } finally {
      eventsLoading = false;
      eventsLoaded = true;
    }
  }

  /**
   * Reset snapshot and clear all stored data
   */
  async function resetSnapshot() {
    localStorage.removeItem(getStorageKey('snapshot'));
    localStorage.removeItem(getStorageKey('events'));
    localStorage.removeItem(getStorageKey('lastEventId'));

    mapState.walls = [];
    mapState.monsterPlacements = [];
    mapState.backgroundUrl = '';
    mapState.gridSize = 70;
    mapState.gamePlayers = [];
    mapState.playerCharacters = [];
    mapState.openings = [];
    mapState.lights = [];
    mapState.covers = [];
    mapState.turnState = null;
    mapState.hook = null;
    events = [];
    lastEventId = null;

    await loadEvents();
  }

  /**
   * Apply an event (from action response)
   * @param {GameEvent} event
   */
  async function applyEvent(event) {
    console.debug('applyEvent called with event:', event);

    // Check if already received via SSE
    if (events.some((e) => e.id === event.id)) {
      console.debug(`Event ${event.id} already in events array, skipping duplicate`);
      localEventIds.add(event.id);
      return;
    }

    // Track as locally-created for deduplication
    localEventIds.add(event.id);

    // Add to events array and save (but NOT lastEventId — that's only
    // saved in processEventQueue after the event is actually processed,
    // so that lastEventId and the snapshot stay in sync)
    events.push(event);
    saveEventsArray();

    // Track as received for gap detection
    if (lastReceivedEventId === null || event.id > lastReceivedEventId) {
      lastReceivedEventId = event.id;
    }

    // Queue for processing
    eventQueue.push(event);
    processEventQueue();
  }

  /**
   * Handle SSE event (real-time event from server)
   * @param {GameEvent} event
   */
  async function handleSSEEvent(event) {
    // game:restarted is a control signal — short-circuit before the
    // gap / dedup checks because the post-restart event sequence
    // resets to 1, while the client's stale lastReceivedEventId is
    // still pointing at the pre-restart tail. Without this
    // short-circuit player 2 would never see the reload.
    if (event.eventType === 'game:restarted') {
      const restartChange = (event.changes || []).find((/** @type {{ path?: string }} */ c) => c?.path === '/restart');
      const restartData =
        /** @type {{ data?: { restarterUserId?: string; restarterDisplayName?: string } }} */ restartChange?.data;
      onGameRestarted?.({
        restarterUserId: restartData?.restarterUserId,
        restarterDisplayName: restartData?.restarterDisplayName,
      });
      return;
    }

    // Use lastReceivedEventId for gap detection (tracks received, not yet processed)
    const effectiveLastId = lastReceivedEventId ?? lastEventId;

    // Skip if this is our own event (already queued for processing via applyEvent)
    if (localEventIds.has(event.id)) {
      console.debug(`[${storagePrefix}] Skipping local event ${event.id}`);
      if (effectiveLastId === null || event.id > effectiveLastId) {
        lastReceivedEventId = event.id;
      }
      return;
    }

    // Skip events we've already received or processed
    if (effectiveLastId !== null && event.id <= effectiveLastId) {
      return;
    }

    // Check for gaps
    if (effectiveLastId !== null && event.id !== effectiveLastId + 1) {
      console.warn(`[${storagePrefix}] Gap detected: expected ${effectiveLastId + 1}, got ${event.id}`);
      return;
    }

    // Track as received (for gap detection of subsequent events)
    lastReceivedEventId = event.id;

    // Add to events array (dedup against events already stored from a previous session)
    if (!events.some((e) => e.id === event.id)) {
      events.push(event);
      saveEventsArray();
    }

    // Queue for processing — lastEventId is updated after processing
    // to keep it in sync with the snapshot
    eventQueue.push(event);
    processEventQueue();
  }

  /**
   * Apply a batch of historical events fetched via REST during the initial
   * game-enter handshake. Each event mutates state via applyEventToState,
   * which never invokes UX callbacks — this is the path that rebuilds a
   * snapshot for a session the user wasn't watching live.
   *
   * Call this AFTER loadEvents() and BEFORE opening the SSE stream, so the
   * stream can pick up only events truly newer than what we've caught up
   * to. After this returns, lastEventId reflects the latest applied event.
   *
   * @param {GameEvent[]} historicalEvents
   */
  async function catchUpStateFromEvents(historicalEvents) {
    if (!historicalEvents || historicalEvents.length === 0) return;
    const sorted = [...historicalEvents].sort((a, b) => a.id - b.id);
    for (const event of sorted) {
      // Skip events we've already processed (e.g. they came through
      // another path or were cached from a prior session).
      if (lastEventId !== null && event.id <= lastEventId) continue;
      await applyEventToState(event);
      lastEventId = event.id;
      if (!events.some((e) => e.id === event.id)) {
        events.push(event);
      }
    }
    saveStateSnapshot();
    saveEventsArray();
    saveLastEventId();
    // Wake subscribers once at the end. The live path notifies per-event
    // for mid-flight animations; for batch catch-up one final notify is
    // enough since nobody saw the intermediate states.
    mapState.notify();
  }

  return {
    get events() {
      return events;
    },
    get lastEventId() {
      return lastEventId;
    },
    get eventsLoading() {
      return eventsLoading;
    },
    get eventsLoaded() {
      return eventsLoaded;
    },
    loadEvents,
    catchUpStateFromEvents,
    resetSnapshot,
    applyEvent,
    handleSSEEvent,
    saveStateSnapshot,
    loadStateSnapshot,
    pause: () => {
      paused = true;
    },
    resume: () => {
      paused = false;
      processEventQueue();
    },
    step: () => {
      paused = true;
      stepOnce = true;
      processEventQueue();
    },
    get paused() {
      return paused;
    },
    get queueLength() {
      return eventQueue.length;
    },
  };
}
